import { ObjectBuilder, WrapPanel } from "@dreamcam/babylon-tools";
import { ScrollHandler } from "../ScrollHandler";
import { MeshName } from "../MeshName";
import { Env } from "../../settings/Env";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { text } from "../../TextResources";
import { LayoutComponentHelper } from "./LayoutComponentHelper";
import { TagsPanelHelper } from "./TagsPanelHelper";
export class VideoTagsPanel {
    constructor(parent, mesh2d, callbacks) {
        this._selectedSpecialTagButtons = [];
        this._selectedTagButtons = [];
        this._unselectedTagButtons = [];
        this._galleryTags = [];
        this._parent = parent;
        this._mesh = mesh2d;
        this._callbacks = callbacks;
        this._scrollHandler = new ScrollHandler([MeshName.lobbyTags], mesh2d.m2px(Env.lobby.tagsPanel.scrollView.speed), (v) => {
            if (this._tagsPanel.scrollViewer !== undefined)
                this._tagsPanel.scrollViewer.verticalValue = Math.max(Math.min(this._tagsPanel.scrollViewer.verticalValue + v / (this._tagsPanel.scrollViewer.children[0].heightInPixels - this._tagsPanel.scrollViewer.heightInPixels), 1), 0);
        });
        this._tagsPanel = TagsPanelHelper.createTagsPanel(this._mesh, parent);
        TagsPanelHelper.createVerticalGap(this._mesh, this._tagsPanel.stackPanel);
        this._selectedSpecialTagsPanel = this._createSelectedSpecialTagsPanel(this._tagsPanel.stackPanel);
        this._tagsPanel.stackPanel.addControl(ObjectBuilder.createTextBlock3D(this._mesh, `videoTagsHeader`, text.lobby_video_tags_header, Env.lobby.tagsPanel.videoTags.header));
        this._selectedTagsPanel = this._createSelectedTagsPanel(this._tagsPanel.stackPanel);
        this._unselectedTagsPanel = this._createUnselectedTagsPanel(this._tagsPanel.stackPanel);
        this._noTagsPanel = TagsPanelHelper.createNoTagsPanel(this._mesh, this._tagsPanel.stackPanel, {
            clearTagGroupsHandler: () => {
                this._selectedTagsPanel.wrapPanel.clearControls();
                this._callbacks.removeAllGalleryTags();
            },
        });
        TagsPanelHelper.createVerticalGap(this._mesh, this._tagsPanel.stackPanel);
    }
    get isVisible() {
        return this._parent.isVisible;
    }
    set isVisible(value) {
        if (value)
            this._scrollHandler.addThumbstickAxesChangedListener();
        else
            this._scrollHandler.releaseThumbstickAxesChangedListener();
        this._parent.isVisible = value;
    }
    updateTags(tags) {
        this._galleryTags = tags;
        this._updateSelectedSpecialTagsPanel();
        this._updateSelectedTagsPanel();
        this._updateUnselectedTagsPanel();
        this._updateNoTagsPanel();
    }
    updateScrollOnPointerEnter() {
        if (this._callbacks.getLobbyMode() !== "Videos")
            return;
        const scrollViewer = this._tagsPanel.scrollViewer;
        scrollViewer.valueToScrollTo = scrollViewer.verticalValue;
        scrollViewer.forceVerticalBar = true;
    }
    updateScrollOnPointerOut() {
        this._scrollHandler.stopScroll();
        const scrollViewer = this._tagsPanel.scrollViewer;
        scrollViewer.forceVerticalBar = false;
        scrollViewer.valueToScrollTo = undefined;
    }
    _createSelectedSpecialTagsPanel(parent) {
        const wrapPanel = new WrapPanel("selectedSpecialTags");
        wrapPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        wrapPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        wrapPanel.isVertical = true;
        wrapPanel.background = Env.lobby.tagsPanel.selectedTagsPanel.background;
        wrapPanel.widthInPixels = this._mesh.m2px(Env.lobby.tagsPanel.width);
        parent.addControl(wrapPanel);
        return { wrapPanel };
    }
    _createSelectedTagsPanel(parent) {
        const wrapPanel = new WrapPanel("selectedTags");
        wrapPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        wrapPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        wrapPanel.isVertical = true;
        wrapPanel.background = Env.lobby.tagsPanel.selectedTagsPanel.background;
        wrapPanel.widthInPixels = this._mesh.m2px(Env.lobby.tagsPanel.width);
        parent.addControl(wrapPanel);
        return { wrapPanel };
    }
    _createUnselectedTagsPanel(parent) {
        const wrapPanel = new WrapPanel("unselectedTags");
        wrapPanel.isVertical = true;
        parent.addControl(wrapPanel);
        return { wrapPanel };
    }
    _updateSelectedSpecialTagsPanel() {
        this._selectedSpecialTagButtons = [];
        this._selectedSpecialTagsPanel.wrapPanel.clearControls();
        const filteredModel = this._callbacks.getGalleryModel();
        if (filteredModel !== null) {
            const modelTag = { code: filteredModel.modelId, name: filteredModel.modelNickname };
            const tagContainer = LayoutComponentHelper.createSelectedTagContainer(this._mesh, modelTag);
            this._selectedSpecialTagButtons.push({ code: modelTag.code, button: tagContainer });
            tagContainer.onClickObserver.add(() => {
                if (typeof navigator !== "undefined" && navigator.onLine === false)
                    return;
                if (this._callbacks.isUpdatingGalleryFilter())
                    return;
                this._disableAllTagButtons();
                setTimeout(() => tagContainer.stateController.changeState("untagged"), 0);
                this._callbacks.setGalleryModel(null);
            });
            this._selectedSpecialTagsPanel.wrapPanel.addControl(tagContainer);
        }
        if (this._selectedSpecialTagButtons.length > 0) {
            const bottomPadding = new Control("topPadding");
            bottomPadding.widthInPixels = this._selectedTagsPanel.wrapPanel.widthInPixels;
            bottomPadding.heightInPixels = this._mesh.m2px(Env.lobby.tagsPanel.selectedTagsPanel.padding.bottom);
            this._selectedSpecialTagsPanel.wrapPanel.addControl(bottomPadding);
        }
    }
    _updateSelectedTagsPanel() {
        this._selectedTagButtons = [];
        this._selectedTagsPanel.wrapPanel.clearControls();
        const selectedTagCodes = this._callbacks.getFilteredGalleryTagCodes();
        const selectedTags = this._galleryTags.filter(x => selectedTagCodes.find(s => s === x.code) !== undefined);
        for (const tag of selectedTags) {
            if (this._selectedTagButtons.find(x => x.code === tag.code) !== undefined)
                continue;
            const tagContainer = LayoutComponentHelper.createSelectedTagContainer(this._mesh, tag);
            this._selectedTagButtons.push({ code: tag.code, button: tagContainer });
            tagContainer.onClickObserver.add(() => {
                if (typeof navigator !== "undefined" && navigator.onLine === false)
                    return;
                if (this._callbacks.isUpdatingGalleryFilter())
                    return;
                if (this._callbacks.getFilteredGalleryTagCodes().find(x => x === tag.code) !== undefined) {
                    this._disableAllTagButtons();
                    setTimeout(() => tagContainer.stateController.changeState("untagged"), 0);
                    this._callbacks.removeGalleryTag(tag.code);
                }
            });
            this._selectedTagsPanel.wrapPanel.addControl(tagContainer);
        }
        if (this._selectedTagButtons.length > 0) {
            const clearAllButton = LayoutComponentHelper.createClearAllTagsButton(this._mesh);
            clearAllButton.onClickObserver.add(() => {
                if (typeof navigator !== "undefined" && navigator.onLine === false)
                    return;
                if (this._callbacks.isUpdatingGalleryFilter())
                    return;
                this._disableAllTagButtons();
                this._selectedTagsPanel.wrapPanel.clearControls();
                this._callbacks.removeAllGalleryTags();
            });
            this._selectedTagsPanel.wrapPanel.addControl(clearAllButton);
            const bottomPadding = new Control("topPadding");
            bottomPadding.widthInPixels = this._selectedTagsPanel.wrapPanel.widthInPixels;
            bottomPadding.heightInPixels = this._mesh.m2px(Env.lobby.tagsPanel.selectedTagsPanel.padding.bottom);
            this._selectedTagsPanel.wrapPanel.addControl(bottomPadding);
        }
    }
    _updateUnselectedTagsPanel() {
        this._unselectedTagButtons = [];
        this._unselectedTagsPanel.wrapPanel.clearControls();
        const selectedTagCodes = this._callbacks.getFilteredGalleryTagCodes();
        const unselectedTags = this._galleryTags.filter(x => selectedTagCodes.find(s => s === x.code) === undefined);
        for (const tag of unselectedTags) {
            const tagContainer = LayoutComponentHelper.createUnselectedTagContainer(this._mesh, tag, true, Env.lobby.tagsPanel.unselectedTagsPanel);
            this._unselectedTagButtons.push({ code: tag.code, button: tagContainer });
            tagContainer.onClickObserver.add(() => {
                if (typeof navigator !== "undefined" && navigator.onLine === false)
                    return;
                if (this._callbacks.isUpdatingGalleryFilter())
                    return;
                const selectedTagCodes = this._callbacks.getFilteredGalleryTagCodes();
                if (selectedTagCodes.find(x => x === tag.code) === undefined) {
                    this._disableAllTagButtons();
                    setTimeout(() => tagContainer.stateController.changeState("tagged"), 0);
                    this._callbacks.addGalleryTag(tag.code);
                }
            });
            this._unselectedTagsPanel.wrapPanel.addControl(tagContainer);
        }
        this._tagsPanel.scrollViewer.verticalValue = 0;
    }
    _updateNoTagsPanel() {
        this._noTagsPanel.stackPanel.isVisible = this._unselectedTagButtons.length === 0;
    }
    _disableAllTagButtons() {
        for (const btn of [
            ...this._selectedSpecialTagButtons,
            ...this._selectedTagButtons,
            ...this._unselectedTagButtons,
        ])
            btn.button.isEnabled = false;
    }
}
